import React, { useRef, useState, useEffect } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Autoplay } from 'swiper/modules';
import Header from '../../../components/header';
import { supabase } from '../../../supabaseClient';
import { useLanguage } from '../../../helpers/language-context';

import 'swiper/css';
import 'swiper/css/navigation';

const Awards = () => {
  const { translations, language } = useLanguage();
  const [awards, setAwards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAward, setSelectedAward] = useState(null);
  const swiperRef = useRef(null);
  const prevRef = useRef(null);
  const nextRef = useRef(null);

  useEffect(() => {
    const fetchAwards = async () => {
      const { data, error } = await supabase
        .from('awards')
        .select('*')
        .order('year', { ascending: false });

      if (error) {
        console.error('Error fetching awards:', error);
      } else {
        setAwards(data);
      }
      setLoading(false);
    };

    fetchAwards();
  }, []);

  const handleAwardClick = (award) => {
    setSelectedAward(award);
    if (swiperRef.current && swiperRef.current.autoplay) {
      swiperRef.current.autoplay.stop();
    }
  };

  const handleCloseModal = () => {
    setSelectedAward(null);
    if (swiperRef.current && swiperRef.current.autoplay) {
      swiperRef.current.autoplay.start();
    }
  };

  return (
    <div className="ourbrand-container">
      <Header title={translations.about.awards} flip={true} bgImg="/assets/images/header-bg/bg_awards.png" />
      <section className="bg-white text-black space-y-16 py-16 px-6 lg:px-20">
        {/* award slider */}
        {loading ? (
          <div className="text-center py-10">Loading awards...</div>
        ) : (
          <div className="relative group">
            <div className="flex items-center justify-end mb-4 space-x-2">
              <button
                ref={prevRef}
                className="w-10 h-10 rounded-full border-2 border-[#ABABAB] bg-[#ABABAB] text-white hover:bg-[#28357B] hover:border-[#28357B] hover:text-white transition-colors duration-300 flex items-center justify-center z-10"
                aria-label="Previous slide"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                ref={nextRef}
                className="w-10 h-10 rounded-full border-2 border-[#ABABAB] bg-[#ABABAB] text-white hover:bg-[#28357B] hover:border-[#28357B] hover:text-white transition-colors duration-300 flex items-center justify-center z-10"
                aria-label="Next slide"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>

            <Swiper
              modules={[Navigation, Autoplay]}
              spaceBetween={24}
              slidesPerView={1}
              loop={true}
              autoplay={{
                delay: 3000,
                disableOnInteraction: false,
              }}
              breakpoints={{
                640: { slidesPerView: 2 },
                768: { slidesPerView: 3 },
                1024: { slidesPerView: 4 },
              }}
              navigation={{
                prevEl: prevRef.current,
                nextEl: nextRef.current,
              }}
              onBeforeInit={(swiper) => {
                swiper.params.navigation.prevEl = prevRef.current;
                swiper.params.navigation.nextEl = nextRef.current;
              }}
              onSwiper={(swiper) => {
                swiperRef.current = swiper;
              }}
              className="awards-swiper"
            >
              {awards.map((award) => (
                <SwiperSlide key={award.id}>
                  <div
                    onClick={() => handleAwardClick(award)}
                    className="flex flex-col h-full bg-gray-50 rounded-xl overflow-hidden hover:shadow-lg transition-shadow duration-300 cursor-pointer"
                  >
                    <div className="aspect-square relative overflow-hidden bg-white">
                      <img
                        src={award.image || '/assets/images/placeholder-award.jpg'}
                        alt={language === 'en' ? award.title_en : award.title_ms}
                        className="h-full w-auto object-cover object-center transform hover:scale-105 transition-transform duration-500 mx-auto p-4"
                        onError={(e) => {
                          e.target.src = '/assets/images/placeholder-award.jpg';
                        }}
                      />
                      <div className="absolute top-0 right-0 bg-[#FDA729] text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                        {award.year}
                      </div>
                    </div>
                    <div className="p-5 flex flex-col flex-grow">
                      <h3 className="text-lg font-bold text-[#28357B] mb-2 line-clamp-2">
                        {language === 'en' ? award.title_en : award.title_ms}
                      </h3>
                      <p className="text-sm text-gray-600 line-clamp-3">
                        {language === 'en' ? award.description_en : award.description_ms}
                      </p>
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        )}

        {/* Award Detail Modal */}
        {selectedAward && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={handleCloseModal}>
            <div
              className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto relative shadow-2xl animate-fade-in"
              onClick={e => e.stopPropagation()}
            >
              <button
                onClick={handleCloseModal}
                className="absolute top-4 right-4 z-10 p-2 bg-white/80 rounded-full hover:bg-gray-100 transition-colors shadow-sm"
              >
                <svg className="w-6 h-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              <div className="flex flex-col md:flex-row">
                {/* Image Section */}
                <div className="w-full md:w-1/2 bg-gray-50 flex items-center justify-center p-8 md:sticky md:top-0 h-auto">
                  <img
                    src={selectedAward.image || '/assets/images/placeholder-award.jpg'}
                    alt={language === 'en' ? selectedAward.title_en : selectedAward.title_ms}
                    className="max-h-[50vh] w-auto object-contain shadow-md rounded-lg"
                  />
                </div>

                {/* Content Section */}
                <div className="w-full md:w-1/2 p-8 md:p-12">
                  <div className="inline-block px-3 py-1 bg-[#FDA729] text-white text-sm font-bold rounded-full mb-4">
                    {selectedAward.year}
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold text-[#28357B] mb-6">
                    {language === 'en' ? selectedAward.title_en : selectedAward.title_ms}
                  </h3>
                  <div className="prose prose-lg text-gray-600">
                    <p className="whitespace-pre-wrap leading-relaxed">
                      {language === 'en' ? selectedAward.description_en : selectedAward.description_ms}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default Awards;
